/* ==========================================================================
   Vision Attire — Shopify bridge
   On the Shopify theme this swaps the catalogue's placeholder stock for the
   store's live variants, adds to the real cart and keeps the bag count. On
   Netlify staging window.Shopify doesn't exist, VA_SHOP stays undefined and
   every page runs exactly as before.

   The catalogue keeps the design data (photos, swatches, copy). Shopify owns
   what it should: variant IDs, prices and whether a size is in stock. The link
   between them is a handle rule plus the overrides in shopify.json:
     colourway `royal-blue` in collection `mens-physique-shorts`
       -> Shopify product `mens-physique-shorts-royal-blue`
     unless shopify.json says e.g. { "product": "bodybuilding-trunks", "colour": "Metallic Blue" }
   ========================================================================== */
(() => {
  'use strict';
  if (!window.Shopify || !Shopify.shop) return;

  const root = (Shopify.routes && Shopify.routes.root) || '/';
  const norm = (s) => String(s ?? '').toLowerCase().replace(/[^a-z0-9.]/g, '');
  const num = (s) => { const m = String(s ?? '').match(/\d+(\.\d+)?/); return m ? +m[0] : null; };
  // Shorts are sized by waist (26") and trunks by letter (M, 2XL): keep a letter as its label.
  const sizeOf = (s) => /^\s*\d+(\.\d+)?\s*("|”|″|in)?\s*$/i.test(String(s ?? '')) ? num(s) : (String(s ?? '').trim().toUpperCase() || null);
  const json = (r) => { if (!r.ok) throw new Error(r.status + ' ' + r.url); return r.json(); };

  const mapReady = fetch(VA_DATA('assets/data/shopify.json')).then(json).catch(() => ({}));
  const products = {};
  const product = (handle) => products[handle] || (products[handle] =
    fetch(root + 'products/' + encodeURIComponent(handle) + '.js').then(json).catch(() => null));

  // Which position holds size, length and colour, by option name.
  function slots(p, names) {
    const list = (p.options || []).map(o => norm(typeof o === 'string' ? o : o.name));
    const find = (keys) => list.findIndex(n => keys.some(k => norm(k) === n));
    return {
      size: find(names.size || ['Size', 'Waist', 'Waist size']),
      length: find(names.length || ['Length']),
      colour: find(names.colour || ['Colour', 'Color', 'Colourway', 'Cut', 'Style']),
    };
  }

  function entryFor(map, coll, cw) {
    const c = (map.colourways || {})[coll.handle] || {}, all = c['*'];
    const o = c[cw.handle] || (all ? { product: all.product, colour: cw.title } : {});
    return { product: o.product || coll.handle + '-' + cw.handle, colour: o.colour || null };
  }

  /* Returns the catalogue with live stock, prices and variants. A colourway
     whose Shopify product can't be found is marked out and flagged `missing`,
     so a mapping mistake shows as "sold out" on the page and a console warning,
     never as an add-to-cart that fails. */
  async function live(cat) {
    const map = await mapReady;
    await Promise.all((cat.collections || []).flatMap(coll => coll.products.map(async cw => {
      const e = entryFor(map, coll, cw), p = await product(e.product);
      if (!p) { cw.stock = 'out'; cw.missing = true; cw.variants = [];
        console.warn('[va-shop] no Shopify product for', coll.handle, cw.handle, '->', e.product); return; }
      const s = slots(p, map.optionNames || {});
      cw.variants = p.variants
        .filter(v => !e.colour || s.colour < 0 || norm(v.options[s.colour]) === norm(e.colour))
        .map(v => ({ id: v.id, available: v.available, price: v.price / 100,
                     size: s.size < 0 ? null : sizeOf(v.options[s.size]),
                     length: s.length < 0 ? null : num(v.options[s.length]) }));
      if (!cw.variants.length) console.warn('[va-shop] no variants match', e);
      const avail = cw.variants.filter(v => v.available);
      cw.stock = avail.length ? (avail.length <= Math.ceil(cw.variants.length / 4) ? 'low' : 'in') : 'out';
      cw.shopifyUrl = p.url;
      if (cw.variants.length) cw.price = Math.min(...cw.variants.map(v => v.price));
    })));
    (cat.collections || []).forEach(c => {
      const prices = c.products.map(p => p.price).filter(x => x != null);
      if (prices.length) c.price = Math.min(...prices);
    });
    return cat;
  }

  // The variant for a size and length, or null. Missing slots on the product
  // (a trunk has no length) match anything.
  function variant(cw, size, length) {
    return (cw.variants || []).find(v =>
      (size == null || v.size == null || String(v.size).toUpperCase() === String(size).toUpperCase()) &&
      (length == null || v.length == null || v.length === +length)) || null;
  }

  // Every size the store sells across a collection's colourways, in shop order:
  // waists ascending, then letters S to 3XL. Empty when nothing mapped.
  const ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', '3XL', '4XL'];
  function sizes(coll) {
    const all = [...new Set(coll.products.flatMap(p => (p.variants || []).map(v => v.size)).filter(x => x != null))];
    const rank = (x) => typeof x === 'number' ? x : 1000 + (ORDER.includes(x) ? ORDER.indexOf(x) : 99);
    return all.sort((a, b) => rank(a) - rank(b));
  }

  /* --- bag -------------------------------------------------------------- */
  let count = 0;
  function paintBag(n) {
    count = n;
    document.querySelectorAll('[data-cart]').forEach(a => {
      a.hidden = false;
      a.classList.toggle('is-full', n > 0);
      a.setAttribute('aria-label', `Your bag, ${n} item${n === 1 ? '' : 's'}`);
      const b = a.querySelector('[data-cart-count]'); if (b) b.textContent = n;
    });
  }
  const refresh = () => fetch(root + 'cart.js').then(json).then(c => paintBag(c.item_count)).catch(() => paintBag(count));

  async function add(items) {
    const r = await fetch(root + 'cart/add.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    const body = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(body.description || body.message || 'Could not add to your bag');
    await refresh();
    return body;
  }

  /* Custom Colour deposit: the deposit product's single variant, plus one
     extras variant per chosen extra. Both are priced in Shopify at half the
     full price, so checkout takes exactly the 50% the builder shows. */
  async function addCustom({ extras = [], properties = {}, expectDeposit }) {
    const map = await mapReady, c = map.custom || {};
    const dep = await product(c.deposit || 'custom-colour-deposit');
    if (!dep || !dep.variants.length) throw new Error('The custom deposit product is not set up in the store yet');
    const items = [{ id: dep.variants[0].id, quantity: 1, properties }];
    let charged = dep.variants[0].price / 100;
    if (extras.length) {
      const ex = await product(c.extras || 'custom-order-extras');
      const names = c.extraOptions || {};
      for (const id of extras) {
        const want = norm(names[id] || id);
        const v = ex && ex.variants.find(x => norm(x.title) === want || x.options.some(o => norm(o) === want));
        if (!v) throw new Error(`The "${names[id] || id}" extra is not set up in the store yet`);
        items.push({ id: v.id, quantity: 1, properties: { 'Design ref': properties['Design ref'] } });
        charged += v.price / 100;
      }
    }
    // The page promised a figure. If the store's prices have drifted from the
    // builder's, stop rather than charge something different.
    if (expectDeposit != null && Math.abs(charged - expectDeposit) > 0.009)
      throw new Error(`The deposit in the store ($${charged.toFixed(2)}) doesn't match this design ($${expectDeposit.toFixed(2)})`);
    return add(items);
  }

  window.VA_SHOP = { live, variant, sizes, add, addCustom, refresh, cartUrl: root + 'cart' };
  refresh();
  // Back-button restores from the page cache show a stale count.
  addEventListener('pageshow', e => { if (e.persisted) refresh(); });
})();
